import { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";

import type { FilterType } from "@/components/@types";
import {
  useFilterChipMutations,
  useFilterChipState,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { SEARCH_FILTER_KEY } from "@/graphql/filters";
import { icons } from "@/objects/@icons";
import { cn } from "@/components/ui/utils";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";

/**
 * How long typing settles before the term is committed.
 *
 * Committing writes the term into the shared ``filters`` list, which is both
 * a new set of query variables and a new URL — so a keystroke-per-commit
 * would fire a request and a history entry for every letter.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The ledger search: a plain field typed into where it sits - after the open
 * list's breadcrumbs in the top bar, or in a bar over a list of its own.
 *
 * The term lives in ``filters`` under ``search`` rather than in state of its
 * own, so it serialises into the URL, clears when the sidebar navigates away
 * and survives a tab switch exactly like the chips. What it is not is a
 * *filter* chip: no column backs it, there is no operator to choose, and it
 * compiles to the ORM input's top-level ``search`` field, which ranks against
 * the ``SearchIndex``. So it is a field of its own, not one of the chips the
 * ``+ Filter`` menu builds.
 */
export function UtilityBarSearch() {
  const { filters } = useFilterChipState();
  const { setFilters } = useFilterChipMutations();

  const committed =
    (filters.find((filter) => filter.key === SEARCH_FILTER_KEY)?.value as
      | string
      | undefined) ?? "";
  const [term, setTerm] = useState(committed);

  // Both refs hold values that change identity on most renders. The debounce
  // below must not list them as dependencies — restarting its timer on every
  // render would push the commit permanently into the future, so the term
  // would never reach the query.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;

  // Follow the committed term when something *else* moves it — navigating,
  // a tab switch that resets filters, the back button. Adjusted during
  // render rather than in an effect so the field never paints a stale term
  // first. ``lastSeen`` is what distinguishes those outside changes from
  // this component's own commit landing back.
  const lastSeenRef = useRef(committed);
  if (committed !== lastSeenRef.current) {
    lastSeenRef.current = committed;
    if (committed !== term) {
      setTerm(committed);
    }
  }

  useEffect(() => {
    if (term === committed) {
      return;
    }
    const timeout = setTimeout(() => {
      lastSeenRef.current = term;
      const rest = filtersRef.current.filter(
        (filter) => filter.key !== SEARCH_FILTER_KEY,
      );
      const searchFilter: FilterType = {
        key: SEARCH_FILTER_KEY,
        type: "text",
        operator: "iContains",
        value: term,
      };
      setFiltersRef.current(term ? [...rest, searchFilter] : rest);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [term, committed]);

  // Clearing drops the term at once rather than after the typing pause:
  // there is nothing left to wait for.
  const clearTerm = () => {
    lastSeenRef.current = "";
    setTerm("");
    setFilters(filters.filter((filter) => filter.key !== SEARCH_FILTER_KEY));
  };

  return (
    <InputGroup
      className={cn(
        "border-0 bg-transparent shadow-none dark:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0 h-8",
        "pl-2 pr-2",
        // Twice as wide while it is typed into, as far as there is room.
        "w-60 max-w-full shrink-0 [transition:width_200ms_ease] focus-within:w-120",
        "text-foreground",
        // Lit under the pointer as a node panel's fields are, and while it is
        // typed into. The theme holds a plain input transparent with
        // ``!important``, so these have to say it as firmly to show.
        "hover:bg-accent! focus-within:bg-accent!",
      )}
    >
      <InputGroupAddon>{<icons.search size={14} />}</InputGroupAddon>
      <InputGroupInput
        value={term}
        placeholder="Search..."
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            (event.target as HTMLElement).blur();
          }
        }}
        aria-label="Search"
      />
      <InputGroupAddon align="inline-end">
        {term ? (
          <InputGroupButton
            size="icon-xs"
            aria-label="Clear search"
            onClick={clearTerm}
          >
            <XIcon />
          </InputGroupButton>
        ) : null}
      </InputGroupAddon>
    </InputGroup>
  );
}
